import { Injectable, NgZone } from '@angular/core';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Observable, fromEvent, map, combineLatest, distinctUntilChanged, startWith } from 'rxjs';

export interface ScreenInfo {
  isPortrait: boolean;
  isLandscape: boolean;
  size: 'XSmall' | 'Small' | 'Medium' | 'Large' | 'XLarge';
  screenWidth: number;
  screenHeight: number;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  constructor(private breakpointObserver: BreakpointObserver, private ngZone: NgZone) { }

  // Observable for orientation
  orientation$: Observable<string> = this.breakpointObserver
    .observe(['(orientation: portrait)', '(orientation: landscape)'])
    .pipe(
      map((state: BreakpointState) => {
        return state.breakpoints['(orientation: portrait)'] ? 'portrait' : 'landscape';
      }),
      distinctUntilChanged()
    );

  // Observable for size (based on Angular CDK breakpoints)
  size$: Observable<string> = this.breakpointObserver
    .observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
    ])
    .pipe(
      map((state: BreakpointState) => {
        if (state.breakpoints[Breakpoints.XSmall]) return 'XSmall';
        if (state.breakpoints[Breakpoints.Small]) return 'Small';
        if (state.breakpoints[Breakpoints.Medium]) return 'Medium';
        if (state.breakpoints[Breakpoints.Large]) return 'Large';
        if (state.breakpoints[Breakpoints.XLarge]) return 'XLarge';
        return 'Medium'; // fallback
      }),
      distinctUntilChanged()
    );

  // Observable for screen width and height
  screenDimensions$: Observable<{ width: number; height: number }> = fromEvent(window, 'resize')
    .pipe(
      startWith(null), // trigger initial value
      map(() => ({
        width: window.innerWidth,
        height: window.innerHeight,
      })),
      distinctUntilChanged((a, b) => a.width === b.width && a.height === b.height)
    );

  // Combined observable
  screenInfo$ = combineLatest([
    this.orientation$,
    this.size$,
    this.screenDimensions$,
  ]).pipe(
    map(([orientation, size, dimensions]) => ({
      isPortrait: orientation === 'portrait',
      isLandscape: orientation === 'landscape',
      size,
      screenWidth: dimensions.width,
      screenHeight: dimensions.height,
    })),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  getDeviceType(): 'phone' | 'tablet' | 'desktop' {
    const ua = navigator.userAgent;

    if (/Mobi|Android/i.test(ua)) {
      if (/Tablet|iPad/i.test(ua)) {
        return 'tablet';
      }
      return 'phone';
    }

    return 'desktop';
  }

    definePaneSizeRight(size) {

    if (size === "XSmall") { return "collapsed" }
    if (size === "Small") { return "38vw" }
    if (size === "Medium") { return "32vw" }
    if (size === "Large") { return "25vw" }
    if (size === "XLarge") { return "20vw" }
    else return 12
  }

  definePaneSizeLeft(size) {

    if (size === "XLarge") { return "14vw" }
    else return "condensed"
  }

    definePaneSizeBottom(size) {

    if (size === "XSmall") { return "xsmall" }
    if (size === "Small") { return "small" }
    if (size === "Medium") { return "medium" }
    if (size === "Large") { return "large" }
    if (size === "XLarge") { return "xlarge" }
    else return 12
  }

  defineModalSize(screen, height, type) {

    switch (screen) {
      case "XSmall":
        if (type === "width") { return "calc(100% - 32px)" }
        if (type === "height") { return "calc(100% - 32px)" }
        else return "100%";
      case "Small":
        if (type === "width") { return "calc(100% - 32px)" }
        if (type === "height") { return "calc(100% - 32px)" }
        else return "100%";
      case "Medium":
        if (type === "width") { return "calc(100% - 32px)" }
        if (type === "height") { return "calc(100% - 32px)" }
        else return "100%";
      case "Large":
        if (type === "width") {
          if (752 <= height) { return "1248px" }
          else { return "calc(100% - 32px)" }
        }
        if (type === "height") {
          if (752 <= height) { return "720px" }
          else { return "calc(100% - 32px)" }
        }
        else return "100%";
      case "XLarge":
        if (type === "width") {
          if (752 <= height) { return "1248px" }
          else { return "calc(100% - 32px)" }
        }
        if (752 <= height) { return "720px" }
        else { return "calc(100% - 32px)" }
      default:
        if (type === "width") {
          if (752 <= height) { return "1248px" }
          else { return "calc(100% - 32px)" }
        }
        if (752 <= height) { return "720px" }
        else { return "calc(100% - 32px)" }
    }
  }
}